import cloneDeep from 'lodash-es/cloneDeep';
import { v4 as uuidv4 } from 'uuid';

/**
 * Recursively removes all keys from the given object that have empty object values.
 *
 * @param {Record<string, any>} obj - The object from which to remove keys.
 * @returns {Record<string, any>} - The modified object with empty objects removed.
 */
export const removeEmptyObjects = (obj: Record<string, unknown>): Record<string, unknown> => {
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      removeEmptyObjects(obj[key] as Record<string, unknown>);

      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    }
  });
  return obj;
};

/**
 * Recursively removes entries from an object that have empty string or undefined values.
 * Works on nested objects and preserves arrays.
 *
 * @param data - The object to clean
 * @returns The object with empty string and undefined values removed at all levels
 *
 * @example
 * ```typescript
 * const input = {
 *   name: 'John',
 *   email: '',
 *   settings: {
 *     theme: 'dark',
 *     language: '',
 *     nested: {
 *       value: undefined,
 *       keep: 'this'
 *     }
 *   }
 * };
 * const result = removeEmptyFieldsDeep(input);
 * // Result: { name: 'John', settings: { theme: 'dark', nested: { keep: 'this' } } }
 * ```
 */
export const removeEmptyFieldsDeep = <T>(data: T, keysToSkip: string[] = []): T => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => removeEmptyFieldsDeep(item, keysToSkip)) as T;
  }

  const result = {} as Record<string, unknown>;

  for (const [key, value] of Object.entries(data)) {
    if (value !== '' && typeof value !== 'undefined') {
      result[key] = removeEmptyFieldsDeep(value, keysToSkip);
    } else if (keysToSkip.includes(key)) {
      result[key] = value;
    }
  }

  return result as T;
};

/**
 * Returns a new array of unique objects from an input array of objects, based on a specified key.
 *
 * @param {Array} arr - The input array of objects.
 * @param {string} key - The key to use for uniqueness comparison.
 * @returns {Array} - A new array of unique objects.
 */
export const getUniqueListBy = (arr, key) => {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
};

/**
 * Splits a filename into its base name and extension.
 * A leading dot (e.g. `.env`) is treated as part of the name, not an extension.
 */
export const splitFileName = (name: string): { baseName: string; ext: string } => {
  const dotIdx = name.lastIndexOf('.');
  return dotIdx > 0
    ? { baseName: name.slice(0, dotIdx), ext: name.slice(dotIdx + 1) }
    : { baseName: name, ext: '' };
};

/**
 * Splits an array into chunks of specified size.
 *
 * @template T
 * @param {T[]} items - The array to split.
 * @param {number} size - The size of each chunk.
 * @returns {T[][]} - The array split into chunks.
 */
export const splitArray = <T>(items: T[], size: number): T[][] => {
  return items.reduce((resultArray: T[][], item, index) => {
    const chunkIndex = Math.floor(index / size);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);
};

const ids = ['node_id', 'action_id', 'condition_id'];

/**
 * Updates the IDs of an object or an array of objects.
 * It recursively traverses and replaces any ID with a new UUID.
 *
 * @param {Object|Array} obj - The object or array of objects to update.
 * @returns {Object|Array} - The updated object or array of objects with new UUIDs.
 */
export const updateIds = (obj) => {
  const clonedObj = cloneDeep(obj);
  const traverse = (o) => {
    for (const i in o) {
      if (typeof o[i] === 'object' && o[i] !== null) {
        if (ids.includes(i)) {
          o[i] = uuidv4();
        }
        traverse(o[i]);
      } else if (Array.isArray(o[i])) {
        o[i].forEach((item) => {
          traverse(item);
        });
      } else {
        if (ids.includes(i)) {
          o[i] = uuidv4();
        }
      }
    }
  };

  traverse(clonedObj);
  return clonedObj;
};

/**
 * Recursively omit specified keys from an object, preserving array structures
 *
 * @param obj - The object to omit keys from
 * @param keysToOmit - Array of keys to omit at any level of nesting
 * @returns New object with specified keys omitted recursively
 *
 * @example
 * ```typescript
 * const data = {
 *   keep: 'this',
 *   remove: 'this',
 *   nested: {
 *     keep: 'this too',
 *     remove: 'not this',
 *     deep: {
 *       remove: 'nor this'
 *     }
 *   },
 *   array: [
 *     { keep: 'item1', remove: 'not this' },
 *     { keep: 'item2', remove: 'not this either' }
 *   ]
 * };
 *
 * const result = omitDeep(data, ['remove']);
 * // Result: {
 * //   keep: 'this',
 * //   nested: { keep: 'this too', deep: {} },
 * //   array: [{ keep: 'item1' }, { keep: 'item2' }]
 * // }
 * ```
 */
export const omitDeep = <T>(obj: T, keysToOmit: string[]): T => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => omitDeep(item, keysToOmit)) as T;
  }

  const result = {} as T;

  for (const [key, value] of Object.entries(obj)) {
    if (!keysToOmit.includes(key)) {
      (result as Record<string, unknown>)[key] = omitDeep(value, keysToOmit);
    }
  }

  return result;
};
